/**
 * Invitation API Service
 * 
 * Encapsulates HTTP communication with invitation endpoints.
 * Hides axios details, URL construction, and error transformation.
 */

import { api } from './api';

// Debug: Log API import
console.log('[DEBUG] InvitationApi: Module loaded');
console.log('[DEBUG] InvitationApi: api imported, type:', typeof api);
console.log('[DEBUG] InvitationApi: api value:', api);
console.log('[DEBUG] InvitationApi: api.post type:', typeof api?.post);

class InvitationApiService {
  /**
   * Send a single invitation
   * @param {Object} data - Invitation data
   * @param {string} data.email - Invitee email
   * @param {string} data.name - Invitee name (optional)
   * @param {string} data.message - Personal message (optional)
   * @param {string} data.role - User role (optional)
   * @param {number} data.expiresInDays - Expiration days (optional)
   * @returns {Promise<Object>} API response
   */
  async sendInvitation(data) {
    try {
      console.log('[DEBUG] InvitationApi.sendInvitation: Starting');
      console.log('[DEBUG] InvitationApi.sendInvitation: api type:', typeof api);
      console.log('[DEBUG] InvitationApi.sendInvitation: api.post type:', typeof api?.post);
      console.log('[DEBUG] InvitationApi.sendInvitation: api object keys:', api ? Object.keys(api) : 'api is null/undefined');
      console.log('[DEBUG] InvitationApi.sendInvitation: data:', data);
      
      // Check token availability before making request
      const tokenFromStorage = localStorage.getItem('accessToken');
      console.log('[DEBUG] InvitationApi.sendInvitation: Token check before request:', {
        tokenPresent: !!tokenFromStorage,
        tokenPreview: tokenFromStorage ? tokenFromStorage.substring(0, 20) + '...' : null,
        localStorageKeys: Object.keys(localStorage)
      });
      
      if (!api || typeof api.post !== 'function') {
        console.error('[DEBUG] InvitationApi.sendInvitation: api.post is not a function!');
        console.error('[DEBUG] InvitationApi.sendInvitation: api value:', api);
        throw new Error('API client is not properly initialized. api.post is not a function.');
      }
      
      console.log('[DEBUG] InvitationApi.sendInvitation: Calling api.post to /invitations');
      const response = await api.post('/invitations', data);
      console.log('[DEBUG] InvitationApi.sendInvitation: Response received:', response);
      console.log('[DEBUG] InvitationApi.sendInvitation: Response type:', typeof response);
      console.log('[DEBUG] InvitationApi.sendInvitation: Response keys:', response ? Object.keys(response) : 'response is null');
      // Note: api.js interceptor returns response.data directly, so response is already the data
      return response;
    } catch (error) {
      console.error('[DEBUG] InvitationApi.sendInvitation: Error caught:', error);
      console.error('[DEBUG] InvitationApi.sendInvitation: Error type:', typeof error);
      console.error('[DEBUG] InvitationApi.sendInvitation: Error message:', error?.message);
      throw this._transformError(error);
    }
  }

  /**
   * Send bulk invitations
   * @param {Array} invitations - Array of invitation objects
   * @param {Object} options - Bulk options (expiresInDays, role)
   * @returns {Promise<Object>} API response
   */
  async sendBulkInvitations(invitations, options = {}) {
    try {
      const response = await api.post('/invitations/bulk', {
        invitations,
        ...options
      });
      // api.js interceptor returns response.data directly
      return response;
    } catch (error) {
      throw this._transformError(error);
    }
  }

  /**
   * Get user's invitations
   * @param {Object} filters - Filter options
   * @param {string} filters.status - Status filter (pending, accepted, expired, cancelled)
   * @param {number} filters.page - Page number
   * @param {number} filters.limit - Items per page
   * @param {string} filters.sortBy - Sort field
   * @param {string} filters.order - Sort order (asc, desc)
   * @returns {Promise<Object>} API response with invitations and pagination
   */
  async getInvitations(filters = {}) {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null) {
          params.append(key, filters[key]);
        }
      });
      
      const response = await api.get(`/invitations?${params.toString()}`);
      // api.js interceptor returns response.data directly
      return response;
    } catch (error) {
      throw this._transformError(error);
    }
  }

  /**
   * Get invitation by ID
   * @param {string} id - Invitation ID
   * @returns {Promise<Object>} API response
   */
  async getInvitation(id) {
    try {
      const response = await api.get(`/invitations/${id}`);
      // api.js interceptor returns response.data directly
      return response;
    } catch (error) {
      throw this._transformError(error);
    }
  }

  /**
   * Resend invitation email
   * @param {string} id - Invitation ID
   * @returns {Promise<Object>} API response
   */
  async resendInvitation(id) {
    try {
      const response = await api.post(`/invitations/${id}/resend`);
      // api.js interceptor returns response.data directly
      return response;
    } catch (error) {
      throw this._transformError(error);
    }
  }

  /**
   * Cancel invitation
   * @param {string} id - Invitation ID
   * @returns {Promise<Object>} API response
   */
  async cancelInvitation(id) {
    try {
      const response = await api.delete(`/invitations/${id}`);
      // api.js interceptor returns response.data directly
      return response;
    } catch (error) {
      throw this._transformError(error);
    }
  }

  /**
   * Validate invitation code (public endpoint)
   * @param {string} code - Invitation code
   * @returns {Promise<Object>} Validation result
   */
  async validateInvitationCode(code) {
    try {
      const response = await api.get(`/invitations/validate/${code}`);
      // api.js interceptor returns response.data directly
      return response;
    } catch (error) {
      throw this._transformError(error);
    }
  }

  /**
   * Accept invitation and create account
   * @param {string} code - Invitation code
   * @param {Object} data - Registration data
   * @param {string} data.password - Password
   * @param {string} data.username - Username
   * @param {string} data.firstName - First name
   * @param {string} data.lastName - Last name
   * @returns {Promise<Object>} API response with user and tokens
   */
  async acceptInvitation(code, data) {
    console.log('[DEBUG] InvitationApi.acceptInvitation: Starting');
    console.log('[DEBUG] InvitationApi.acceptInvitation: Code:', code);
    console.log('[DEBUG] InvitationApi.acceptInvitation: Data:', {
      username: data.username,
      firstName: data.firstName,
      lastName: data.lastName,
      passwordLength: data.password?.length || 0
    });
    console.log('[DEBUG] InvitationApi.acceptInvitation: API base URL:', api.defaults?.baseURL);
    console.log('[DEBUG] InvitationApi.acceptInvitation: Request URL:', `/invitations/accept/${code}`);

    try {
      console.log('[DEBUG] InvitationApi.acceptInvitation: Calling api.post...');
      const startTime = Date.now();
      const response = await api.post(`/invitations/accept/${code}`, data);
      const elapsedTime = Date.now() - startTime;
      
      console.log('[DEBUG] InvitationApi.acceptInvitation: API call completed after', elapsedTime, 'ms');
      console.log('[DEBUG] InvitationApi.acceptInvitation: Response received:', {
        success: response?.success,
        hasUser: !!response?.user,
        hasTokens: !!response?.tokens,
        message: response?.message,
        responseKeys: response ? Object.keys(response) : null
      });
      // api.js interceptor returns response.data directly
      return response;
    } catch (error) {
      console.error('[DEBUG] InvitationApi.acceptInvitation: ERROR caught:', error);
      console.error('[DEBUG] InvitationApi.acceptInvitation: Error details:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        code: error?.code,
        config: error?.config ? {
          url: error.config.url,
          method: error.config.method,
          timeout: error.config.timeout
        } : null
      });
      throw this._transformError(error);
    }
  }

  /**
   * Get invitation statistics
   * @returns {Promise<Object>} Statistics data
   */
  async getInvitationStats() {
    try {
      const response = await api.get('/invitations/stats');
      // api.js interceptor returns response.data directly
      return response;
    } catch (error) {
      throw this._transformError(error);
    }
  }

  /**
   * Transform error from API response
   * @param {Error} error - Axios error
   * @returns {Error} Transformed error
   */
  _transformError(error) {
    if (error.response) {
      // Server responded with error
      const message = error.response.data?.error || error.response.data?.message || 'An error occurred';
      const code = error.response.data?.code;
      const err = new Error(message);
      err.status = error.response.status;
      err.code = code;
      err.details = error.response.data;
      return err;
    } else if (error.request) {
      // Request made but no response
      const err = new Error('Network error. Please check your connection.');
      err.status = 0;
      return err;
    } else {
      // Error in request setup
      return error;
    }
  }
}

// Export singleton instance
export default new InvitationApiService();
